import { z } from 'zod';
import { cursorPageSchema, geoPointSchema, severitySchema } from './common.js';
export const jobTypeSchema = z.enum([
    'INSTALL',
    'REPAIR',
    'SURVEY',
    'MAINTENANCE',
    'INSPECTION',
    'DISCONNECT',
    'RELOCATION',
]);
export const jobStatusSchema = z.enum([
    'DRAFT',
    'ASSIGNED',
    'ACCEPTED',
    'EN_ROUTE',
    'ON_SITE',
    'DONE',
    'CANCELLED',
    'FAILED',
]);
export const jobCreateSchema = z.object({
    type: jobTypeSchema,
    title: z.string().min(3).max(200),
    description: z.string().max(8000).optional(),
    priority: severitySchema.default('P3'),
    incidentId: z.string().uuid().optional(),
    assignedTo: z.string().uuid().optional(),
    siteId: z.string().uuid().optional(),
    uispClientId: z.string().uuid().optional(),
    location: geoPointSchema.optional(),
    address: z.string().max(500).optional(),
    scheduledAt: z.coerce.date().optional(),
});
export const jobUpdateSchema = z.object({
    status: jobStatusSchema.optional(),
    assignedTo: z.string().uuid().nullable().optional(),
    scheduledAt: z.coerce.date().nullable().optional(),
    completionNotes: z.string().max(8000).optional(),
    /** Captured at status change so arrival can be verified against the site. */
    location: geoPointSchema.optional(),
});
export const jobFilterSchema = cursorPageSchema.extend({
    assigned: z.enum(['me', 'any']).default('any'),
    status: z.string().optional(),
    type: jobTypeSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
export const jobMaterialSchema = z.object({
    itemCode: z.string().max(60).optional(),
    name: z.string().min(1).max(200),
    quantity: z.number().positive(),
    unit: z.string().min(1).max(20),
    serial: z.string().max(120).optional(),
});
export const jobSignatureSchema = z.object({
    signerName: z.string().min(1).max(160),
    signerRole: z.string().max(80).optional(),
    /** Data URL from the signature canvas; stored as an image server-side. */
    imageData: z.string().startsWith('data:image/'),
});
/**
 * Offline sync batch.
 *
 * Every operation carries a client-generated `clientId`. That is the
 * idempotency key: replaying a queued batch after a tunnel flap updates the
 * same rows rather than creating duplicates. Without it, a technician with
 * patchy signal quietly produces three copies of one job completion.
 */
export const fieldSyncSchema = z.object({
    operations: z
        .array(z.object({
        clientId: z.string().min(8).max(80),
        /** Client clock. Used for ordering within the batch, not as the server time. */
        occurredAt: z.coerce.date(),
        type: z.enum([
            'JOB_STATUS',
            'JOB_NOTES',
            'JOB_MATERIAL',
            'JOB_SIGNATURE',
            'POLE_INSPECTION',
            'SPLICE_UPDATE',
        ]),
        jobId: z.string().uuid().optional(),
        payload: z.record(z.unknown()),
    }))
        .min(1)
        .max(200),
});
/**
 * Per-operation results. Partial success is the normal case — one malformed
 * row must not reject a technician's whole day of queued work.
 */
export const fieldSyncResultSchema = z.object({
    results: z.array(z.object({
        clientId: z.string(),
        status: z.enum(['APPLIED', 'DUPLICATE', 'CONFLICT', 'REJECTED']),
        message: z.string().optional(),
        serverId: z.string().uuid().optional(),
    })),
});
/**
 * Offline job packet: everything a technician needs with no signal.
 * Downloaded on assignment, not on arrival — the site is usually where the
 * signal is worst.
 */
export const jobPacketSchema = z.object({
    job: z.object({
        id: z.string().uuid(),
        ref: z.string(),
        type: jobTypeSchema,
        title: z.string(),
        description: z.string().nullable(),
        priority: severitySchema,
        status: jobStatusSchema,
        scheduledAt: z.coerce.date().nullable(),
        address: z.string().nullable(),
        location: geoPointSchema.nullable(),
    }),
    customer: z
        .object({
        name: z.string(),
        phone: z.string().nullable(),
        plan: z.string().nullable(),
        address: z.string().nullable(),
    })
        .nullable(),
    fiberPath: z
        .array(z.object({
        sequence: z.number().int(),
        kind: z.string(),
        label: z.string(),
        lossDb: z.number(),
        cumulativeLossDb: z.number(),
    }))
        .optional(),
    spliceDiagram: z.unknown().optional(),
    route: z.unknown().optional(),
    materials: z.array(jobMaterialSchema).optional(),
    contacts: z.array(z.object({ name: z.string(), role: z.string(), phone: z.string() })).optional(),
});
//# sourceMappingURL=field.js.map